import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, Euro, Users, Clock, MapPin, ArrowRight } from "lucide-react";

interface Stats {
  totalBookings: number;
  pendingBookings: number;
  totalRevenue: number;
  todayBookings: number;
}

interface BookingRow {
  id: string;
  client_name: string;
  client_phone: string | null;
  booking_date: string;
  booking_time: string;
  status: string;
  location_type: string | null;
  address: string | null;
  total_price: number | null;
  services?: { name: string } | null;
  staff?: { name: string } | null;
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmada",
  on_the_way: "A caminho",
  completed: "Concluída",
  cancelled: "Cancelada",
  blocked: "Bloqueado",
};

const STATUS_CLASSES: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  on_the_way: "bg-sky-100 text-sky-800",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/10 text-destructive",
  blocked: "bg-muted text-muted-foreground",
};

const formatDate = (d: string) => {
  const m = d?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};

const weekdayLabel = (d: string) => {
  const date = new Date(`${d}T12:00:00`);
  return date.toLocaleDateString("pt-PT", { weekday: "short" });
};

const BookingCard = ({ b }: { b: BookingRow }) => (
  <div className="rounded-xl border border-border bg-background p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-foreground truncate">{b.client_name}</p>
        <p className="text-sm text-muted-foreground truncate">{b.services?.name || "Serviço"}</p>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_CLASSES[b.status] || "bg-muted text-muted-foreground"}`}>
        {STATUS_LABELS[b.status] || b.status}
      </span>
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
      <span className="inline-flex items-center gap-1.5 capitalize">
        <Calendar size={14} /> {weekdayLabel(b.booking_date)} {formatDate(b.booking_date)}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Clock size={14} /> {(b.booking_time || "").slice(0, 5)}
      </span>
      <span className="inline-flex items-center gap-1.5 truncate">
        <MapPin size={14} />
        {b.location_type === "home" ? b.address || "Domicílio" : "Espaço Mente Sã"}
      </span>
    </div>
    {(b.staff?.name || b.total_price != null) && (
      <div className="mt-2 flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{b.staff?.name || ""}</span>
        {b.total_price != null && (
          <span className="font-semibold text-foreground">€{Number(b.total_price).toFixed(2)}</span>
        )}
      </div>
    )}
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState<Stats>({
    totalBookings: 0,
    pendingBookings: 0,
    totalRevenue: 0,
    todayBookings: 0,
  });
  const [pending, setPending] = useState<BookingRow[]>([]);
  const [nextWeek, setNextWeek] = useState<BookingRow[]>([]);

  useEffect(() => {
    const fetchAll = async () => {
      const now = new Date();
      const today = now.toISOString().split("T")[0];
      const in7 = new Date(now.getTime() + 7 * 86400000).toISOString().split("T")[0];
      const select =
        "id, client_name, client_phone, booking_date, booking_time, status, location_type, address, total_price, services(name), staff(name)";

      const [allBookings, pendingBookings, todayBookings, weekBookings] = await Promise.all([
        supabase.from("bookings").select("total_price, status"),
        supabase
          .from("bookings")
          .select(select)
          .eq("status", "pending")
          .order("booking_date", { ascending: true })
          .order("booking_time", { ascending: true }),
        supabase.from("bookings").select("id").eq("booking_date", today),
        supabase
          .from("bookings")
          .select(select)
          .gte("booking_date", today)
          .lte("booking_date", in7)
          .in("status", ["pending", "confirmed", "on_the_way"])
          .order("booking_date", { ascending: true })
          .order("booking_time", { ascending: true }),
      ]);

      const completed = (allBookings.data || []).filter(
        (b: any) => (b.status === "completed" || b.status === "confirmed") && b.status !== "blocked"
      );
      const revenue = completed.reduce((sum: number, b: any) => sum + Number(b.total_price), 0);

      const totalBookingsFiltered = (allBookings.data || []).filter((b: any) => b.status !== "blocked").length;

      setStats({
        totalBookings: totalBookingsFiltered,
        pendingBookings: pendingBookings.data?.length || 0,
        totalRevenue: revenue,
        todayBookings: todayBookings.data?.length || 0,
      });
      setPending((pendingBookings.data as any) || []);
      setNextWeek((weekBookings.data as any) || []);
    };
    fetchAll();
  }, []);

  const cards = [
    { icon: Calendar, label: "Total Reservas", value: stats.totalBookings, color: "bg-primary/10 text-primary" },
    { icon: Clock, label: "Pendentes", value: stats.pendingBookings, color: "bg-amber-100 text-amber-700" },
    { icon: Euro, label: "Receita", value: `€${stats.totalRevenue.toFixed(2)}`, color: "bg-emerald-100 text-emerald-700" },
    { icon: Users, label: "Hoje", value: stats.todayBookings, color: "bg-accent text-accent-foreground" },
  ];

  return (
    <div>
      <h1 className="font-display text-3xl font-bold mb-8">Dashboard</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-6">
            <div className={`w-10 h-10 rounded-xl ${c.color} flex items-center justify-center mb-4`}>
              <c.icon size={20} />
            </div>
            <p className="text-muted-foreground text-sm">{c.label}</p>
            <p className="font-display text-2xl font-bold text-foreground mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Pendentes de confirmação */}
      <section className="mt-10 rounded-2xl border border-amber-300/60 bg-amber-50/60 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold flex items-center gap-2 text-amber-900">
            <Clock size={20} /> Pendentes de confirmação
            <span className="rounded-full bg-amber-200 px-2 py-0.5 text-sm text-amber-900">{pending.length}</span>
          </h2>
          <Link to="/admin/agenda" className="text-sm font-medium text-amber-900 inline-flex items-center gap-1 hover:underline">
            Ver na agenda <ArrowRight size={14} />
          </Link>
        </div>
        {pending.length === 0 ? (
          <p className="text-sm text-amber-900/70">Sem reservas pendentes.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pending.slice(0, 6).map((b) => (
              <BookingCard key={b.id} b={b} />
            ))}
          </div>
        )}
      </section>

      {/* Próximos 7 dias */}
      <section className="mt-8 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Calendar size={20} /> Próximos 7 dias
            <span className="rounded-full bg-muted px-2 py-0.5 text-sm text-muted-foreground">{nextWeek.length}</span>
          </h2>
          <Link to="/admin/agenda" className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline">
            Ver agenda <ArrowRight size={14} />
          </Link>
        </div>
        {nextWeek.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sem reservas nos próximos 7 dias.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {nextWeek.slice(0, 9).map((b) => (
              <BookingCard key={b.id} b={b} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
